import React, { useState, useEffect, useMemo } from "react";
import Question from "components/quiz/Question";
import QuizWrapper from "components/quizBackgrounds/QuizWrapper";
import CurrentPlayerDisplay from "components/quiz/quizLocalMultiplayer/CurrentPlayerDisplay";
import ScoreBoard from "components/quiz/quizLocalMultiplayer/ScoreBoard";
import Dice from "components/quiz/quizLocalMultiplayer/Dice";
import { fetchQuestions } from "services/questions";

import { useSelector, useDispatch } from "react-redux";
import { setRoomReducer } from "../redux/room/actions";
import EndQuizMultiplayer from "components/quiz/EndQuizMultiplayer";
import OrtivisActionCard from "components/quiz/questions/ortivis/flipCard/OrtivisActionCard";
import soundManager from "audio/OrtivisSounds";
import { updateRoom } from "services/rooms";
import { IoMdVolumeHigh, IoMdVolumeMute } from "react-icons/io";
import { RandomPicker } from "utils/randomPicker";
import { fetchQuizWrapper } from "services/quizWrappers";

const specialCases = [
	{
		type: "move_backward",
		move_value: -2,
		text: "Une visite surprise de l'ANCOLS retarde vos projets RSE. Reculez de 2 cases !",
	},
	{
		type: "move_backward",
		move_value: -3,
		text: "Aie, la commune vous a refusé l'autorisation de vente d'un logement inscrit au plan de vente du PSP. Reculez de 3 cases !",
	},
	{
		type: "move_backward",
		move_value: -3,
		text: "Oh non, une fuite d'eau non signalée occasionne beaucoup de perte et des dégâts dans un logement vacant. Reculez de 3 cases !",
	},
	{
		type: "move_backward",
		move_value: -2,
		text: "Mince, votre responsable RSE n'a pas demandé l'avis des collaborateurs pour les temps forts. Reculez de 2 cases !",
	},
	{
		type: "move_backward",
		move_value: -3,
		text: "Aie, des incivilités ont eu lieu dans les parties communes. À vous de resensibiliser vos locataires. Reculez de 3 cases.",
	},
	{
		type: "move_backward",
		move_value: -2,
		text: "Malgré les consignes de tri, vos locataires ne les respectent pas. Reculez de 2 cases !",
	},
	{
		type: "loose_points",
		points_amount: -20,
		text: "Votre politique RSE n'est pas partagée avec vos équipes, perdez 20 points !",
	},
	{
		type: "loose_points",
		points_amount: -20,
		text: "Aie, la commune vous a refusé l'autorisation de démolition d'un immeuble collectif, perdez 20 points !",
	},
	{
		type: "loose_points",
		points_amount: -30,
		text: "Ouille, un article fait la première page dans la presse locale suite à une panne de chauffage prolongée. Perdez 30 points !",
	},
	{
		type: "loose_points",
		points_amount: -10,
		text: "Mince, vous avez oublié de contacter la commune pour l'organisation des Séniolympiades. Perdez 10 points !",
	},
	{
		type: "move_forward",
		move_value: 3,
		text: "La banque vous accorde 0,05 point de bonus sur votre taux d'intérêt grâce à votre politique RSE, avancez de 3 cases !",
	},
	{
		type: "move_forward",
		move_value: 2,
		text: "Vous avez convié vos parties prenantes lors de votre dernier temps fort, bravo ! Avancez de 2 cases",
	},
	{
		type: "move_forward",
		move_value: 2,
		text: "Vous bénéficiez d'un vélo fonction et venez désormais au travail à vélo. Bravo pour cet éco-geste ! Avancez de 2 cases",
	},
	{
		type: "move_forward",
		move_value: 2,
		text: "Votre responsable RSE a demandé l'avis des collaborateurs pour le choix des temps forts. Avancez de 2 cases !",
	},
	{
		type: "move_forward",
		move_value: 2,
		text: "Vous avez organisé un moment d'échange dans un jardin partagé entre voisins, avancez de 2 cases !",
	},
	{
		type: "move_forward",
		move_value: 3,
		text: "Bravo, grâce à une co-construction réussie avec nos fournisseurs, nous avons atteint 50% de matériels bio-sourcés dans nos projets de rénovation. Avancez de 3 cases !",
	},
	{
		type: "earn_points",
		points_amount: 15,
		text: "Grâce à l'engagement de tous les collaborateurs, le tri des déchets est parfaitement réalisé. Gagnez 15 points !",
	},
	{
		type: "earn_points",
		points_amount: 20,
		text: "Le challenge Don du sang a regroupé 10% de vos salariés. Bravo pour votre engagement ! Gagnez 20 points !",
	},
	{
		type: "earn_points",
		points_amount: 25,
		text: "Votre patron a mis en place 'un café avec…' vous permettant de le rencontrer et d'échanger en toute transparence avec lui ! Gagnez 25 points !",
	},
	{
		type: "earn_points",
		points_amount: 20,
		text: "Super, Dijon Métropole vous a accordé une subvention de 100000€ au titre d'un projet de construction Sérénitis. Gagnez 20 points !",
	},
];

const specialCasesPicker = new RandomPicker(specialCases);

const QuizLocalMultiplayerPage = () => {
	const dispatch = useDispatch();
	const room = useSelector((state) => state.roomReducer);
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		// TODO should be id here
		if (room && !room.name) {
			const storedRoom = JSON.parse(localStorage.getItem("room"));
			if (storedRoom) {
				dispatch(setRoomReducer(storedRoom));
			}
		}
	}, [room]);

	const quiz = useMemo(
		() => JSON.parse(localStorage.getItem("quiz")) || {},
		[]
	);
	const cachedWrapper = useMemo(
		() => JSON.parse(localStorage.getItem("quizWrapper")) || null,
		[]
	);
	const [quizWrapper, setQuizWrapper] = useState(cachedWrapper);

	const players = room?.players || [];
	const boardCases = room?.board_cases || [];
	const currentPlayer = players[room?.current_player_index];

	const [questions, setQuestions] = useState([]);
	const [questionsPicker, setQuestionsPicker] = useState(null);
	const [currentQuestion, setCurrentQuestion] = useState(null);
	const [hideDice, setHideDice] = useState(false);
	const [gameOver, setGameOver] = useState(false);

	const [actionCard, setActionCard] = useState(null);
	const [isMuted, setIsMuted] = useState(soundManager.isMuted());

	const totalQuestions = questions.length;

	useEffect(() => {
		soundManager.playLoopSound("ambiantMusic", 1);
	}, []);

	useEffect(() => {
		const fetchWrapper = async () => {
			console.log("fetchWrapper", quiz.quiz_wrapper_id);
			const { data, error } = await fetchQuizWrapper(quiz.quiz_wrapper_id);
			if (error) throw new Error("Erreur lors du chargement du quiz wrapper.");
			localStorage.setItem("quizWrapper", JSON.stringify(data));
			setQuizWrapper(data);
		};

		if (
			quiz.quiz_wrapper_id &&
			cachedWrapper?.organization_id !== quiz?.organization_id
		)
			fetchWrapper();
	}, [quiz, cachedWrapper]);

	// Load questions
	useEffect(() => {
		setHideDice(false);
		fetchQuestions(quiz.id)
			.then((data) => {
				if (!data || data.length === 0) {
					throw new Error("No questions available for this quiz.");
				}
				setQuestions(data);
				let _questionsPicker = new RandomPicker(data);
				setQuestionsPicker(_questionsPicker);
			})
			.catch(console.error);
	}, [quiz]);

	const rollDice = (diceResult) => {
		setHideDice(true);
		movePlayer(diceResult);
	};

	const handleActionCard = (card) => {
		if (card.type === "move_forward" || card.type === "move_backward") {
			movePlayer(card.move_value);
		}
		if (card.type === "loose_points" || card.type === "earn_points") {
			const updatedPlayers = players.map((p, i) =>
				i === room.current_player_index
					? { ...p, score: p.score + card.points_amount }
					: p
			);
			handleNextTurnWPlayers(updatedPlayers);
		}

		setActionCard(null);
	};

	const handleCaseAction = (caseIndex) => {
		let caseData = boardCases[caseIndex];
		setHideDice(true);
		if (!caseData) return;

		if (caseData.type === "bonus") {
			let _card = specialCasesPicker.getRandomItem();
			setActionCard(_card);
		} else if (caseData.type === "question") {
			let _currentQuestion = questionsPicker.getRandomItem();
			setCurrentQuestion(_currentQuestion);
		}
	};

	const movePlayer = async (offset) => {
		const newPosition = Math.max(
			0,
			Math.min(currentPlayer.position + offset, boardCases.length - 1)
		);

		const updatedPlayers = players.map((p, i) =>
			i === room.current_player_index ? { ...p, position: newPosition } : p
		);
		updateGameState(updatedPlayers);
	};

	const onMoveAnimationComplete = (caseIndex) => {
		if (caseIndex === boardCases.length - 1) {
			const updatedPlayers = players.map((p, i) =>
				i === room.current_player_index ? { ...p, score: p.score + 70 } : p
			);
			updateGameState(updatedPlayers);
			setGameOver(true);
			setHideDice(true);
			soundManager.playSound("victory");

			updateRoom(room.id, {
				players: updatedPlayers,
			});
			return;
		}

		setTimeout(() => {
			handleCaseAction(caseIndex);
			setHideDice(true);
		}, 500);
	};

	const handleAnswer = (answerValue) => {
		if (!currentQuestion) return;
		let _addedScore = 0;

		if (currentQuestion.type === "single-choice") {
			const selectedOption = currentQuestion.options.find(
				(option) => option.value === answerValue
			);

			if (selectedOption?.isCorrect) {
				_addedScore += currentQuestion.points;
				soundManager.playSound("correct");
			} else {
				soundManager.playSound("error");
			}
		} else if (currentQuestion.type === "multiple-choice") {
			const correctAnswers = currentQuestion.options
				.filter((option) => option.isCorrect)
				.map((option) => option.value);

			if (Array.isArray(answerValue) && answerValue.length > 0) {
				// Check if this specific quiz should apply per-answer scoring
				if (
					quiz.id === "bb792d8c-5be1-4a72-9ebd-8807dee0d79d" &&
					currentQuestion.type === "multiple-choice"
				) {
					let correctCount = answerValue.filter((value) =>
						correctAnswers.includes(value)
					).length;
					_addedScore += correctCount * 10; // 10 points per correct answer

					if (correctCount === answerValue.length) {
						soundManager.playSound("correct");
					} else {
						soundManager.playSound("error");
					}
				} else {
					// Default scoring (all-or-nothing)
					const isFullyCorrect =
						answerValue.every((value) => correctAnswers.includes(value)) &&
						correctAnswers.every((value) => answerValue.includes(value));

					if (isFullyCorrect) {
						_addedScore += currentQuestion.points;
					}
				}
			}
		} else {
			_addedScore += currentQuestion.points;
		}

		const updatedPlayers = players.map((p, i) =>
			i === room.current_player_index
				? {
						...p,
						score: p.score + _addedScore,
						responses: {
							...p.responses,
							[currentQuestion.id]: answerValue,
						},
				  }
				: p
		);
		updateGameState(updatedPlayers);
	};

	const handleNextTurn = () => {
		const updatedRoom = {
			...room,
			current_player_index:
				(room.current_player_index + 1) % room.players.length,
		};
		dispatch(setRoomReducer(updatedRoom));
		setCurrentQuestion(null);
		setHideDice(false);
	};

	const handleNextTurnWPlayers = (updatedPlayers) => {
		const updatedRoom = {
			...room,
			players: updatedPlayers,
			current_player_index:
				(room.current_player_index + 1) % room.players.length,
		};
		dispatch(setRoomReducer(updatedRoom));
		setCurrentQuestion(null);
		setHideDice(false);
	};

	const updateGameState = (updatedPlayers) => {
		const updatedRoom = { ...room, players: updatedPlayers };
		dispatch(setRoomReducer(updatedRoom));
		///localStorage.setItem("room", JSON.stringify(updatedRoom));
	};

	const handleMute = () => {
		if (soundManager.isMuted()) {
			setIsMuted(false);
			soundManager.unmuteAll();
		} else {
			setIsMuted(true);
			soundManager.muteAll();
		}
	};

	const forceGameOver = () => {
		setGameOver(true);
		const updatedPlayers = players.map((p, i) =>
			i === room.current_player_index ? { ...p, score: p.score + 70 } : p
		);
		updateGameState(updatedPlayers);
		setGameOver(true);
		setHideDice(true);
		soundManager.playSound("victory");

		updateRoom(room.id, {
			players: updatedPlayers,
		});
		setShowModal(false);
	};

	const handleForceGameOverClick = () => {
		setShowModal(true);
	};

	console.log("quizWrapper", quizWrapper);

	return (
		<>
			<div
				className={`relative h-full w-full flex justify-center items-center transition ${
					showModal ? "pointer-events-none" : ""
				}`}
			>
				<QuizWrapper
					quizWrapper={quizWrapper}
					quizOrganizationId={quiz?.organization_id}
					room={room}
					onMoveComplete={onMoveAnimationComplete}
				/>

				<div className="absolute top-4 left-4 flex gap-4">
					{quiz.id === "bb792d8c-5be1-4a72-9ebd-8807dee0d79d" && (
						<button
							onClick={handleMute}
							className="flex items-center justify-center rounded-md h-12 w-12 text-xl p-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
						>
							{isMuted ? <IoMdVolumeMute /> : <IoMdVolumeHigh />}
						</button>
					)}
					{quiz.id === "bb792d8c-5be1-4a72-9ebd-8807dee0d79d" && !gameOver && (
						<button
							onClick={handleForceGameOverClick}
							className="flex items-center justify-center rounded-md text-xl p-2 border-black text-white bg-red-500 hover:bg-red-600 shadow-[6px_6px_0px_rgba(0,0,0,0.7)]  disabled:bg-slate-300"
						>
							Finir la partie
						</button>
					)}
				</div>

				<div className="absolute top-4 right-4 flex flex-col">
					{!gameOver && (
						<ScoreBoard players={players} currentPlayer={currentPlayer} />
					)}
				</div>

				<div className="absolute flex flex-col z-10 top-16 items-center">
					{!gameOver && <CurrentPlayerDisplay player={currentPlayer} />}
					{quizWrapper?.config?.use_dice && !hideDice && !gameOver && (
						<div className="p-6">
							<Dice onRoll={rollDice} />
						</div>
					)}
					{currentQuestion && (
						<div className="flex flex-col items-center">
							<Question
								quiz={quiz}
								question={currentQuestion}
								totalQuestions={totalQuestions}
								onAnswer={handleAnswer}
								handleNext={handleNextTurn}
							/>
						</div>
					)}
					{actionCard && (
						<OrtivisActionCard
							card={actionCard}
							onValidate={handleActionCard}
						/>
					)}
					{gameOver && (
						<EndQuizMultiplayer
							quizId={quiz.id}
							room={room}
							questions={questions}
						/>
					)}
				</div>
			</div>
			{showModal && (
				<div
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
					}}
					className="z-50 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 pointer-events-none"
				>
					<div className="bg-white p-6 rounded-lg shadow-lg w-96 pointer-events-auto">
						<h2 className="text-xl font-semibold mb-4">Terminer la partie</h2>
						<p className="mb-4">Voulez-vous vraiment terminer cette session?</p>
						<div className="flex justify-end space-x-4">
							<button
								onClick={() => setShowModal(false)}
								className="flex items-center justify-center rounded-md p-2 border-black text-white bg-[#fbb554] hover:bg-[#ffa256] disabled:bg-slate-300 shadow-[6px_6px_0px_rgba(0,0,0,0.7)]"
							>
								Non
							</button>
							<button
								onClick={forceGameOver}
								className="flex items-center justify-center rounded-md p-2 border-black text-white bg-red-500 hover:bg-red-600  shadow-[6px_6px_0px_rgba(0,0,0,0.7)]"
							>
								Oui, terminer
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default QuizLocalMultiplayerPage;
